import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { CommonModule } from '@angular/common';
import { buildWaterfallOption, responseAtPath } from './waterfall-chart.model';

@Component({
  selector: 'app-waterfall-chart',
  imports: [DataErrorComponent, CommonModule],
  templateUrl: './waterfall-chart.component.html',
  styleUrl: './waterfall-chart.component.scss'
})
export class WaterfallChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private myChart?: echarts.ECharts;
  private subscription = new Subscription();
  private request?: Subscription;
  private observer?: ResizeObserver;
  public hasError = false;
  public errorMessage = '';
  public selectedStep: { label: string; value: number } | null = null;
  private resizeHandler = () => this.myChart?.resize();

  constructor(private configService: ConfigService, private globalFilterService: GlobalFilterService, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.loadAndRenderChart();
    this.subscription.add(this.globalFilterService.filter$.subscribe(filters => {
      if (Object.keys(filters).length) this.loadAndRenderChart(filters);
    }));
    window.addEventListener('resize', this.resizeHandler);
    const element = document.getElementById(this.data.widgetId);
    if (element) {
      this.observer = new ResizeObserver(this.resizeHandler);
      this.observer.observe(element);
    }
    this.cdr.detectChanges();
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;
    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
      this.myChart.on('click', (event: any) => {
        if (this.data.interaction?.showSelection && event.componentType === 'series' && Number.isFinite(event.data?.rawValue)) {
          this.selectedStep = { label: event.name, value: event.data.rawValue };
          this.cdr.detectChanges();
        }
      });
    }
    this.request?.unsubscribe();
    this.hasError = false;
    this.selectedStep = null;
    const source = this.data.dataSource;
    if (source?.mode === 'static') {
      this.renderRecords(source.records);
      return;
    }
    this.myChart.showLoading();
    if (source) {
      if (!source.apiUrl || (!source.directApi && !source.functionName)) {
        this.showError(this.data.labels?.apiNotConfigured || 'Configure the waterfall API.');
        return;
      }
      this.request = this.configService.fetchData(source.apiUrl, source.functionName,
        { ...source.filters, ...filters }, source, source.method || 'post').subscribe({
        next: response => this.renderRecords(responseAtPath(response, source.responsePath)),
        error: () => this.showError(this.data.labels?.error || 'No data available for the chart.'),
      });
      return;
    }
    // Preserve the original API/series contract for other waterfall widgets.
    this.request = this.configService.getConfig(this.data.apiUrl, filters).subscribe({
      next: response => {
        const chartData = response?.data;
        if (!chartData || (Array.isArray(chartData) && !chartData.length)) {
          this.showError('No data found for the selected filters.');
          return;
        }
        this.renderLegacyChart(chartData);
      },
      error: () => this.showError('No data available for the chart.'),
    });
  }

  private renderRecords(records: any): void {
    if (!Array.isArray(records) || !records.length) {
      this.showError(this.data.labels?.empty || 'No investment data available.');
      return;
    }
    try {
      this.myChart?.setOption(buildWaterfallOption(this.data, records), true);
      this.myChart?.hideLoading();
    } catch {
      this.showError(this.data.labels?.invalidData || 'Invalid waterfall data or field mapping.');
    }
  }

  private renderLegacyChart(chartData: any): void {
    this.myChart?.setOption({
      title: {},
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const target = params[1]?.value !== '-' ? params[1] : params[2];
          return target ? `${target.name}<br/>${target.seriesName} : ${target.value}` : '';
        },
      },
      legend: { data: ['Expenses', 'Income'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: chartData.category },
      yAxis: { type: 'value' },
      series: chartData.series,
    });
    this.myChart?.hideLoading();
  }

  private showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.myChart?.hideLoading();
    this.myChart?.clear();
  }

  formatSelectedValue(value: number): string {
    return new Intl.NumberFormat(this.data.numberFormat?.locale || 'en-US', {
      maximumFractionDigits: this.data.numberFormat?.decimals ?? 2,
    }).format(value);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.request?.unsubscribe();
    this.observer?.disconnect();
    window.removeEventListener('resize', this.resizeHandler);
    this.myChart?.dispose();
  }
}
